export interface ParseUdiOutput {
  isUDI: boolean
  isIsbt128: boolean
  deviceId?: string | null
  manufacturingDate?: string | null
  expirationDate?: string | null
  lotBatch?: string | null
  serialNumber?: string | null
  isbt128ProcessorCode?: string | null
}

// Define a union type for valid date kinds
type DateKind = 'manufacturingDate' | 'expirationDate'

export function parseUdi(input: string): ParseUdiOutput {
  // Normalize input by replacing ASCII space with '+'
  input = input.replaceAll(String.fromCharCode(32), '+')

  const parsedValue: ParseUdiOutput = {
    isUDI: false,
    isIsbt128: false,
    deviceId: null,
    manufacturingDate: null,
    expirationDate: null,
    lotBatch: null,
    serialNumber: null,
    isbt128ProcessorCode: null,
  }

  // GS1 UDI regex
  const gs1UdiRegex = /^\(01\).+/
  if (gs1UdiRegex.test(input)) {
    parsedValue.isUDI = true
    parseGs1Udi(input, parsedValue)
  }

  // GS1 UDI without brackets
  const gs1UdiRegexNoBrackets = /^01\d{14,}/
  if (gs1UdiRegexNoBrackets.test(input)) {
    parsedValue.isUDI = true
    parseGs1UdiNoBrackets(input, parsedValue)
  }

  // GS1 UDI without brackets and not starting with "01"
  const gs1UdiRegexNoBracketsNo01 = /^\d{14}$/
  if (gs1UdiRegexNoBracketsNo01.test(input)) {
    parsedValue.isUDI = true
    parsedValue.deviceId = input
  }

  // HIBCC UDI
  const hibccUdiRegex = /^\+[A-Za-z0-9].+/
  if (hibccUdiRegex.test(input)) {
    parsedValue.isUDI = true
    parseHibccUdi(input, parsedValue)
  }

  // ISBT 128 UDI
  const isbt128Regex = /^\=\+.+/
  if (isbt128Regex.test(input)) {
    parsedValue.isIsbt128 = true
    parseIsbt128Udi(input, parsedValue)
  }

  return parsedValue
}

function parseGs1Udi(input: string, parsedValue: ParseUdiOutput) {
  const udiSplit = input.split('(').filter((item) => item)
  for (const part of udiSplit) {
    switch (part.substring(0, 2)) {
      case '01':
        parsedValue.deviceId = part.substring(3)
        break
      case '10':
        parsedValue.lotBatch = part.substring(3)
        break
      case '11':
        parsedValue.manufacturingDate = formatDate(part, 3)
        break
      case '17':
        parsedValue.expirationDate = formatDate(part, 3)
        break
      case '21':
        parsedValue.serialNumber = part.substring(3)
        break
    }
  }
}

function parseGs1UdiNoBrackets(input: string, parsedValue: ParseUdiOutput) {
  parsedValue.deviceId = input.substring(2, 16)
  if (input.length > 18) {
    let cursor = 16
    let parsing = true
    while (parsing) {
      let dateKind: DateKind | null = null // Use the union type here
      const type = input.substring(cursor, cursor + 2)
      if (type === '11') {
        dateKind = 'manufacturingDate'
      } else if (type === '17') {
        dateKind = 'expirationDate'
      }

      if (dateKind) {
        parsedValue[dateKind] = formatDate(input, cursor + 2)
        cursor += 8
      } else if (type === '10' || type === '21') {
        const remainder = input.substring(cursor + 2)
        const delimiterIndex = remainder.indexOf('\\u001d')
        parsedValue[type === '10' ? 'lotBatch' : 'serialNumber'] =
          delimiterIndex >= 0
            ? remainder.substring(0, delimiterIndex)
            : remainder
        cursor += delimiterIndex >= 0 ? delimiterIndex + 8 : remainder.length
        parsing = false
      } else {
        parsing = false
      }
    }
  }
}

function parseHibccUdi(input: string, parsedValue: ParseUdiOutput) {
  const udiSplit = input.split('/')
  parsedValue.deviceId = udiSplit[0].substring(1)
  // Special handling for Axogen spec
  if (
    parsedValue.deviceId.startsWith('M462') &&
    udiSplit[1] &&
    udiSplit[1].length === 13
  ) {
    parsedValue.expirationDate = convertJulianToCalString(
      'C' + udiSplit[1].substring(0, 5)
    )
    parsedValue.lotBatch = udiSplit[1].substring(5, 12)
  }
}

function parseIsbt128Udi(input: string, parsedValue: ParseUdiOutput) {
  const split = input.split('=')
  for (const part of split) {
    switch (part.substring(0, 1)) {
      case '/':
        parsedValue.isbt128ProcessorCode = part.substring(1)
        break
      case ',':
        parsedValue.serialNumber = part.substring(1)
        break
      case '>':
        parsedValue.expirationDate = convertJulianToCalString(part.substring(1))
        break
    }
  }
}

function formatDate(part: string, startIndex: number): string | null {
  if (part.length < startIndex + 6) return null
  return `20${part.substring(startIndex, startIndex + 2)}-${part.substring(
    startIndex + 2,
    startIndex + 4
  )}-${part.substring(startIndex + 4, startIndex + 6)}`
}

const convertJulianToCalString = (julian: string): string | null => {
  try {
    const year = julian.substring(1, 3).toString()
    const days = julian.substring(3, 6)
    const calendarDate = new Date(`20${year}-01-01`)
    calendarDate.setDate(calendarDate.getDate() + parseInt(days))
    return calendarDate.toISOString().substring(0, 10)
  } catch (error: any) {
    return null
  }
}
